/**
 * Configurable scalar technicals for the shared indicator library.
 *
 * Each is a `SharedIndicatorDef` whose `computeSeries` is INDEX-ALIGNED to
 * `data.closes` (length === closes.length, `null` during warmup). The math
 * wraps the SAME `technicalindicators` calls the scanner worker already uses in
 * `lib/scanner/indicators.ts` (computePanelRow), so the last finite value at
 * each indicator's DEFAULT params reproduces the matching `scanner_panel`
 * column exactly:
 *
 *   RSI(14)            ≡ rsi_14            %vs SMA(50)  ≡ vs_sma_50_pct
 *   ROC(10)            ≡ roc_10            %vs SMA(200) ≡ vs_sma_200_pct
 *   MFI(14)            ≡ mfi_14            ADX(14)      ≡ adx_14
 *   Stoch %K(14)       ≡ stoch_k          CCI(20)      ≡ cci_20
 *   Williams %R(14)    ≡ williamsr_14     MACD hist    ≡ macd_hist
 *   Bollinger %B(20,2) ≡ bb_pctb          BB width     ≡ bb_width
 *   HV%(20)            ≡ hv_20            Vol z-score(20) ≡ vol_z_20
 *
 * These power the chart (full series, user params) AND the scanner's on-demand
 * configurable columns (last value at user params, computed server-side). The
 * worker is NOT re-plumbed onto these yet — parity is verified instead.
 */
import {
  SMA,
  EMA,
  RSI,
  MACD,
  BollingerBands,
  ATR,
  ADX,
  Stochastic,
  CCI,
  WilliamsR,
  MFI,
  ROC,
} from "technicalindicators";
import type { OHLCV, IndicatorParams, SharedIndicatorDef } from "./types";
import { realizedVol } from "@/lib/realizedVol";

// ── helpers ──────────────────────────────────────────────────────────────

/** Right-align a `technicalindicators` output (which trims the warmup) back to
 *  a length-`n` series with leading `null`s, so out[i] tracks closes[i]. */
function alignRight(values: ReadonlyArray<number | null>, n: number): (number | null)[] {
  const out: (number | null)[] = new Array(n).fill(null);
  const offset = n - values.length;
  if (offset < 0) return out; // shouldn't happen; guard anyway
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    out[offset + i] = typeof v === "number" && Number.isFinite(v) ? v : null;
  }
  return out;
}

/** ±1000% data-quality cap mirroring the scanner's pctReturn — keeps
 *  reverse-split / sub-penny artifacts from polluting a %-distance column. */
function pctOrNull(latest: number, base: number): number | null {
  if (!Number.isFinite(base) || base === 0) return null;
  const r = ((latest - base) / base) * 100;
  if (!Number.isFinite(r) || Math.abs(r) > 1000) return null;
  return r;
}

function num(v: number | undefined, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function len(v: number | undefined, fallback: number): number {
  return Math.max(1, Math.round(num(v, fallback)));
}

function stdevPop(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

// ── trend ────────────────────────────────────────────────────────────────

/** Percent distance of the close above/below its N-period SMA.
 *  Default 50 ≡ scanner `vs_sma_50_pct`; 200 ≡ `vs_sma_200_pct`. */
export const PCT_VS_SMA: SharedIndicatorDef = {
  id: "vs-sma",
  label: "% vs SMA",
  group: "Trend",
  format: "pct",
  heat: true,
  referenceLevels: [0],
  params: [{ id: "length", label: "Length", kind: "number", default: 50, min: 1, max: 400, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const n = closes.length;
    const period = len(params.length, 50);
    const sma = alignRight(SMA.calculate({ period, values: closes }), n);
    const out: (number | null)[] = new Array(n).fill(null);
    for (let i = 0; i < n; i++) {
      const m = sma[i];
      if (m != null) out[i] = pctOrNull(closes[i]!, m);
    }
    return out;
  },
};

/** Percent distance of the close above/below its N-period EMA. */
export const PCT_VS_EMA: SharedIndicatorDef = {
  id: "vs-ema",
  label: "% vs EMA",
  group: "Trend",
  format: "pct",
  heat: true,
  referenceLevels: [0],
  params: [{ id: "length", label: "Length", kind: "number", default: 50, min: 1, max: 400, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const n = closes.length;
    const period = len(params.length, 50);
    const ema = alignRight(EMA.calculate({ period, values: closes }), n);
    const out: (number | null)[] = new Array(n).fill(null);
    for (let i = 0; i < n; i++) {
      const m = ema[i];
      if (m != null) out[i] = pctOrNull(closes[i]!, m);
    }
    return out;
  },
};

/** ADX (Average Directional Index) — trend strength, 0..100.
 *  Default 14 ≡ scanner `adx_14`. */
export const ADX_INDICATOR: SharedIndicatorDef = {
  id: "adx",
  label: "ADX",
  group: "Trend",
  format: "num",
  referenceLevels: [20, 40],
  params: [{ id: "length", label: "Length", kind: "number", default: 14, min: 2, max: 100, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, highs, lows } = data;
    const n = closes.length;
    if (!highs || !lows) return new Array(n).fill(null);
    const period = len(params.length, 14);
    const series = ADX.calculate({ period, high: highs, low: lows, close: closes });
    const adx = series.map((v) => (v && Number.isFinite(v.adx) ? v.adx : null));
    return alignRight(adx, n);
  },
};

// ── momentum ───────────────────────────────────────────────────────────────

/** RSI — Wilder's relative strength index, 0..100. Default 14 ≡ `rsi_14`. */
export const RSI_INDICATOR: SharedIndicatorDef = {
  id: "rsi",
  label: "RSI",
  group: "Momentum",
  format: "num",
  referenceLevels: [30, 70],
  params: [{ id: "length", label: "Length", kind: "number", default: 14, min: 2, max: 100, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const period = len(params.length, 14);
    return alignRight(RSI.calculate({ period, values: closes }), closes.length);
  },
};

/** Rate of change, percent over N bars. Default 10 ≡ `roc_10`. */
export const ROC_INDICATOR: SharedIndicatorDef = {
  id: "roc",
  label: "ROC",
  group: "Momentum",
  format: "pct",
  heat: true,
  referenceLevels: [0],
  params: [{ id: "length", label: "Length", kind: "number", default: 10, min: 1, max: 200, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const period = len(params.length, 10);
    return alignRight(ROC.calculate({ period, values: closes }), closes.length);
  },
};

/** Stochastic %K (fast), 0..100. Default 14 ≡ `stoch_k` (signalPeriod only
 *  affects %D, not %K, so it's fixed at 3 here). */
export const STOCH_K_INDICATOR: SharedIndicatorDef = {
  id: "stoch-k",
  label: "Stochastic %K",
  group: "Momentum",
  format: "num",
  referenceLevels: [20, 80],
  params: [{ id: "length", label: "Length", kind: "number", default: 14, min: 1, max: 100, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, highs, lows } = data;
    const n = closes.length;
    if (!highs || !lows) return new Array(n).fill(null);
    const period = len(params.length, 14);
    const series = Stochastic.calculate({ high: highs, low: lows, close: closes, period, signalPeriod: 3 });
    const k = series.map((v) => (v && Number.isFinite(v.k) ? v.k : null));
    return alignRight(k, n);
  },
};

/** Williams %R, −100..0. Default 14 ≡ `williamsr_14`. */
export const WILLIAMS_R_INDICATOR: SharedIndicatorDef = {
  id: "williams-r",
  label: "Williams %R",
  group: "Momentum",
  format: "num",
  referenceLevels: [-80, -20],
  params: [{ id: "length", label: "Length", kind: "number", default: 14, min: 1, max: 100, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, highs, lows } = data;
    const n = closes.length;
    if (!highs || !lows) return new Array(n).fill(null);
    const period = len(params.length, 14);
    return alignRight(WilliamsR.calculate({ period, high: highs, low: lows, close: closes }), n);
  },
};

/** CCI (Commodity Channel Index), unbounded, sign-meaningful.
 *  Default 20 ≡ `cci_20`. */
export const CCI_INDICATOR: SharedIndicatorDef = {
  id: "cci",
  label: "CCI",
  group: "Momentum",
  format: "signed",
  signColored: true,
  referenceLevels: [-100, 0, 100],
  params: [{ id: "length", label: "Length", kind: "number", default: 20, min: 1, max: 200, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, highs, lows } = data;
    const n = closes.length;
    if (!highs || !lows) return new Array(n).fill(null);
    const period = len(params.length, 20);
    return alignRight(CCI.calculate({ period, high: highs, low: lows, close: closes }), n);
  },
};

/** MACD histogram (MACD − signal), sign-meaningful. Defaults 12/26/9 ≡
 *  `macd_hist`. */
export const MACD_HIST_INDICATOR: SharedIndicatorDef = {
  id: "macd-hist",
  label: "MACD histogram",
  group: "Momentum",
  format: "signed",
  signColored: true,
  referenceLevels: [0],
  params: [
    { id: "fast", label: "Fast", kind: "number", default: 12, min: 1, max: 200, step: 1 },
    { id: "slow", label: "Slow", kind: "number", default: 26, min: 1, max: 400, step: 1 },
    { id: "signal", label: "Signal", kind: "number", default: 9, min: 1, max: 100, step: 1 },
  ],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const n = closes.length;
    const fastPeriod = len(params.fast, 12);
    const slowPeriod = len(params.slow, 26);
    const signalPeriod = len(params.signal, 9);
    const series = MACD.calculate({
      values: closes,
      fastPeriod,
      slowPeriod,
      signalPeriod,
      SimpleMAOscillator: false,
      SimpleMASignal: false,
    });
    const hist = series.map((v) => (v && Number.isFinite(v.histogram as number) ? (v.histogram as number) : null));
    return alignRight(hist, n);
  },
};

/** Money Flow Index, 0..100. Default 14 ≡ `mfi_14`. */
export const MFI_INDICATOR: SharedIndicatorDef = {
  id: "mfi",
  label: "Money Flow Index",
  group: "Volume",
  format: "num",
  referenceLevels: [20, 80],
  params: [{ id: "length", label: "Length", kind: "number", default: 14, min: 1, max: 100, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, highs, lows, volumes } = data;
    const n = closes.length;
    if (!highs || !lows) return new Array(n).fill(null);
    const period = len(params.length, 14);
    return alignRight(
      MFI.calculate({ high: highs, low: lows, close: closes, volume: volumes, period }),
      n,
    );
  },
};

// ── volatility ───────────────────────────────────────────────────────────

/** ATR as a percent of the close (normalized so it's comparable across
 *  price levels). Default 14; the underlying ATR ≡ scanner `atr_14`. */
export const ATR_PCT_INDICATOR: SharedIndicatorDef = {
  id: "atr-pct",
  label: "ATR %",
  group: "Volatility",
  format: "percent",
  params: [{ id: "length", label: "Length", kind: "number", default: 14, min: 1, max: 100, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, highs, lows } = data;
    const n = closes.length;
    if (!highs || !lows) return new Array(n).fill(null);
    const period = len(params.length, 14);
    const atr = alignRight(ATR.calculate({ period, high: highs, low: lows, close: closes }), n);
    const out: (number | null)[] = new Array(n).fill(null);
    for (let i = 0; i < n; i++) {
      const a = atr[i];
      const c = closes[i]!;
      if (a != null && c > 0) out[i] = (a / c) * 100;
    }
    return out;
  },
};

/** Bollinger %B = (close − lower) / (upper − lower). 0 = lower band,
 *  1 = upper band. Defaults 20/2 ≡ `bb_pctb`. */
export const BB_PCTB_INDICATOR: SharedIndicatorDef = {
  id: "bb-pctb",
  label: "Bollinger %B",
  group: "Volatility",
  format: "ratio",
  referenceLevels: [0, 0.5, 1],
  params: [
    { id: "length", label: "Length", kind: "number", default: 20, min: 2, max: 200, step: 1 },
    { id: "mult", label: "StdDev mult", kind: "number", default: 2, min: 0.5, max: 5, step: 0.5 },
  ],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const n = closes.length;
    const period = len(params.length, 20);
    const stdDev = num(params.mult, 2);
    const bb = BollingerBands.calculate({ period, stdDev, values: closes });
    const aligned = alignRightObj(bb, n);
    const out: (number | null)[] = new Array(n).fill(null);
    for (let i = 0; i < n; i++) {
      const b = aligned[i];
      if (b && b.upper !== b.lower) out[i] = (closes[i]! - b.lower) / (b.upper - b.lower);
    }
    return out;
  },
};

/** Bollinger band width = (upper − lower) / middle × 100. Defaults 20/2 ≡
 *  `bb_width`. */
export const BB_WIDTH_INDICATOR: SharedIndicatorDef = {
  id: "bb-width",
  label: "Bollinger width",
  group: "Volatility",
  format: "percent",
  params: [
    { id: "length", label: "Length", kind: "number", default: 20, min: 2, max: 200, step: 1 },
    { id: "mult", label: "StdDev mult", kind: "number", default: 2, min: 0.5, max: 5, step: 0.5 },
  ],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const n = closes.length;
    const period = len(params.length, 20);
    const stdDev = num(params.mult, 2);
    const bb = BollingerBands.calculate({ period, stdDev, values: closes });
    const aligned = alignRightObj(bb, n);
    const out: (number | null)[] = new Array(n).fill(null);
    for (let i = 0; i < n; i++) {
      const b = aligned[i];
      if (b && b.middle) out[i] = ((b.upper - b.lower) / b.middle) * 100;
    }
    return out;
  },
};

/** Annualized historical volatility (%) from log returns over a trailing
 *  window. Default 20 ≡ `hv_20`. */
export const HV_PCT_INDICATOR: SharedIndicatorDef = {
  id: "hv-pct",
  label: "Historical volatility %",
  group: "Volatility",
  format: "percent",
  params: [{ id: "length", label: "Length", kind: "number", default: 20, min: 2, max: 252, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes } = data;
    const n = closes.length;
    const period = len(params.length, 20);
    const out: (number | null)[] = new Array(n).fill(null);
    // Each bar: the app's one realised volatility (lib/realizedVol.ts) of the
    // `period` returns ending on it, the figure the scanner bakes as hv_20.
    for (let i = period; i < n; i++) {
      const v = realizedVol(closes.slice(i - period, i + 1), period);
      if (v != null) out[i] = v * 100;
    }
    return out;
  },
};

// ── volume ───────────────────────────────────────────────────────────────

/** Volume z-score: today's volume vs the mean/SD of the prior N bars
 *  (excluding today). Default 20 ≡ `vol_z_20`. */
export const VOL_Z_INDICATOR: SharedIndicatorDef = {
  id: "vol-z",
  label: "Volume z-score",
  group: "Volume",
  format: "signed",
  signColored: true,
  referenceLevels: [0, 2],
  params: [{ id: "length", label: "Length", kind: "number", default: 20, min: 2, max: 252, step: 1 }],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { volumes } = data;
    const n = volumes.length;
    const period = len(params.length, 20);
    const out: (number | null)[] = new Array(n).fill(null);
    for (let i = period; i < n; i++) {
      const window = volumes.slice(i - period, i); // prior `period`, excludes i
      const mean = window.reduce((a, b) => a + b, 0) / window.length;
      const sd = stdevPop(window);
      if (sd > 0 && Number.isFinite(volumes[i]!)) out[i] = (volumes[i]! - mean) / sd;
    }
    return out;
  },
};

// BollingerBands returns objects, not numbers — its own right-align helper.
type BBOut = { middle: number; upper: number; lower: number };
function alignRightObj(values: ReadonlyArray<BBOut>, n: number): (BBOut | null)[] {
  const out: (BBOut | null)[] = new Array(n).fill(null);
  const offset = n - values.length;
  if (offset < 0) return out;
  for (let i = 0; i < values.length; i++) out[offset + i] = values[i] ?? null;
  return out;
}

/** CP-CC — per-bar change percent: C-C (prev close → close, default) or O-C
 *  (this bar's open → close). The scanner column shows the latest bar's value. */
export const CP_CC_INDICATOR: SharedIndicatorDef = {
  id: "cp-cc",
  label: "Afiat - Change %",
  group: "Momentum",
  format: "pct",
  heat: true,
  referenceLevels: [0],
  params: [
    {
      id: "mode",
      label: "Calculation",
      kind: "select",
      default: 0,
      choices: [
        { value: 0, label: "C-C (prev close → close)" },
        { value: 1, label: "O-C (open → close)" },
      ],
    },
  ],
  computeSeries(data: OHLCV, params: IndicatorParams): (number | null)[] {
    const { closes, opens } = data;
    const n = closes.length;
    const oc = (params.mode ?? 0) === 1;
    const out = new Array<number | null>(n).fill(null);
    for (let i = oc ? 0 : 1; i < n; i++) {
      const base = oc ? (opens?.[i] ?? NaN) : closes[i - 1]!;
      const cur = closes[i]!;
      if (base > 0) out[i] = ((cur - base) / base) * 100;
    }
    return out;
  },
};

/** All configurable scalar technicals, in picker order. fM + RVol live in their
 *  own modules and are concatenated in index.ts. */
export const TECHNICAL_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  RSI_INDICATOR,
  ROC_INDICATOR,
  CP_CC_INDICATOR,
  STOCH_K_INDICATOR,
  WILLIAMS_R_INDICATOR,
  CCI_INDICATOR,
  MACD_HIST_INDICATOR,
  PCT_VS_SMA,
  PCT_VS_EMA,
  ADX_INDICATOR,
  ATR_PCT_INDICATOR,
  BB_PCTB_INDICATOR,
  BB_WIDTH_INDICATOR,
  HV_PCT_INDICATOR,
  MFI_INDICATOR,
  VOL_Z_INDICATOR,
];
